import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_ID = "db7sibf3hiaolq192l9g";
const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db7sibf3hiaolq192l9g";

const CONTENT_SCOPES = [
  { icon: "ri-movie-2-line", title: "伦理电影", desc: "韩国伦理题材长片，涵盖爱情、社会、心理等方向的剧情与影评。" },
  { icon: "ri-tv-2-line", title: "伦理电视剧", desc: "连续剧集形式的伦理题材作品，提供集数、剧情与主题介绍。" },
  { icon: "ri-chat-quote-line", title: "影评精选", desc: "来自影评人与编辑的观察解读，帮助理解作品背后的伦理命题。" },
  { icon: "ri-bookmark-line", title: "编辑专题", desc: "按主题策划的作品合集，便于系统浏览与发现。" },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们 - 韩国伦理影视内容平台介绍",
    description:
      "了解「韩国伦理」平台定位、内容范围与使用说明，包含版权声明、隐私政策摘要及联系方式与反馈入口。",
    keywords: "韩国伦理关于我们,韩国伦理平台介绍,伦理影视内容,版权声明,隐私政策",
  });

  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("company_alt") ?? "").trim();
    if (honey) {
      setFormStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, typeof value === "string" ? value : "");
    });

    setFormStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = String(
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "",
      ).trim();
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setFormStatus("success");
        setFormError("");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试，或直接邮件联系我们。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，提交失败，请稍后重试，或直接邮件联系我们。");
    }
  };

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-5 mb-8 max-w-3xl">
        <span className="inline-block px-2.5 py-1 rounded-md bg-primary-100 text-primary-800 text-xs font-medium mb-4">
          关于平台
        </span>
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
          关于「韩国伦理」
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-700 leading-7">
          「韩国伦理」是一家聚焦韩国伦理电影及伦理专题电视剧的内容平台，主要面向喜爱韩国伦理影视作品的成年观众、影视影评爱好者及专题探索用户。我们希望帮助用户高效地查找、浏览剧情介绍、影评与专题推荐，更深入地了解并发现优质的韩国伦理影视内容。
        </p>
      </header>

      <section className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-1 h-6 rounded-full bg-accent-500" />
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">内容范围</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CONTENT_SCOPES.map((scope) => (
            <div
              key={scope.title}
              className="rounded-lg border border-background-200 bg-background-100/50 p-5 flex flex-col gap-3"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${scope.icon} text-xl`} />
              </span>
              <h3 className="font-heading font-semibold text-foreground-950">{scope.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{scope.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-copyright-line text-lg" />
            </span>
            <h2 className="font-heading font-bold text-lg text-foreground-950">版权声明</h2>
          </div>
          <div className="flex flex-col gap-3 text-sm text-foreground-700 leading-7">
            <p>
              本站所展示的影视作品名称、剧情介绍、影评与专题内容均为演示示例，仅供产品功能演示与界面预览使用，不代表任何真实作品的官方信息。
            </p>
            <p>
              站内使用的封面图与配图均为示意插画，不涉及任何真实影视海报。若你需要将本站用于正式运营，请确保所使用的文字与图片素材已获得合法授权。
            </p>
            <p>禁止将本站内容用于任何商业转载、二次分发或误导性宣传。</p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-shield-check-line text-lg" />
            </span>
            <h2 className="font-heading font-bold text-lg text-foreground-950">隐私政策摘要</h2>
          </div>
          <div className="flex flex-col gap-3 text-sm text-foreground-700 leading-7">
            <p>
              本站不要求注册登录，也不收集你的个人身份信息。收藏功能基于浏览器本地存储实现，仅保存在当前设备与浏览器中，不会上传到服务器。
            </p>
            <p>
              当你通过反馈表单主动联系我们时，所填写的信息仅用于回复你的问题与建议，我们不会将其用于其他用途或对外提供。
            </p>
            <p>你可以随时清除浏览器数据以删除本地收藏记录。</p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-8">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-6 rounded-full bg-accent-500" />
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
              联系方式与反馈
            </h2>
          </div>
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 flex items-start gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className="ri-mail-line text-lg" />
              </span>
              <div>
                <p className="font-medium text-foreground-900">邮件联系</p>
                <p className="text-sm text-foreground-600 mt-1">
                  内容合作、纠错建议或版权反馈，可发送邮件至
                  <span className="text-primary-700"> feedback@hanguo-lunli.com</span>
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 flex items-start gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className="ri-time-line text-lg" />
              </span>
              <div>
                <p className="font-medium text-foreground-900">回复时间</p>
                <p className="text-sm text-foreground-600 mt-1">
                  我们通常会在 2 - 3 个工作日内回复你的反馈，感谢你的耐心与支持。
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 flex items-start gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className="ri-information-line text-lg" />
              </span>
              <div>
                <p className="font-medium text-foreground-900">温馨提示</p>
                <p className="text-sm text-foreground-600 mt-1">
                  本站为内容展示站点，不提供播放、下载或在线支付服务，请勿在表单中提交相关诉求。
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h3 className="font-heading font-bold text-lg text-foreground-950 mb-1">给我们留言</h3>
          <p className="text-sm text-foreground-500 mb-6">
            填写下方表单即可提交反馈，我们会尽快查看你的留言。
          </p>

          <form
            id={FORM_ID}
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="about-name" className="text-sm text-foreground-700">
                称呼
              </label>
              <input
                id="about-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的称呼"
                className="h-11 px-3 rounded-md border border-background-300 bg-background-100 outline-none text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="about-email" className="text-sm text-foreground-700">
                邮箱
              </label>
              <input
                id="about-email"
                name="email"
                type="email"
                required
                placeholder="用于接收回复的邮箱"
                className="h-11 px-3 rounded-md border border-background-300 bg-background-100 outline-none text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="about-topic" className="text-sm text-foreground-700">
                反馈类型
              </label>
              <select
                id="about-topic"
                name="topic"
                defaultValue="内容纠错"
                className="h-11 px-3 rounded-md border border-background-300 bg-background-100 outline-none text-sm text-foreground-800 focus:border-primary-400 cursor-pointer transition-colors"
              >
                <option value="内容纠错">内容纠错</option>
                <option value="内容合作">内容合作</option>
                <option value="版权反馈">版权反馈</option>
                <option value="功能建议">功能建议</option>
                <option value="其他问题">其他问题</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="about-message" className="text-sm text-foreground-700">
                留言内容
              </label>
              <textarea
                id="about-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请简单描述你的问题或建议（最多 500 字）"
                className="px-3 py-2.5 rounded-md border border-background-300 bg-background-100 outline-none text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 resize-none transition-colors"
              />
            </div>

            <input
              className="legacy-field-note"
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={formStatus === "submitting"}
              className="h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {formStatus === "submitting" ? "提交中..." : "提交反馈"}
            </button>

            {formStatus === "success" && (
              <div className="rounded-md bg-accent-100 text-accent-900 text-sm px-4 py-3 flex items-center gap-2">
                <i className="ri-checkbox-circle-line" /> 提交成功，感谢你的反馈，我们会尽快查看。
              </div>
            )}
            {formStatus === "error" && (
              <div className="rounded-md bg-primary-100 text-primary-800 text-sm px-4 py-3 flex items-center gap-2">
                <i className="ri-error-warning-line" /> {formError}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}